import React, { useState, useCallback, useEffect } from "react";
import * as yup from "yup";
import ClientDetails from "./ClientDetails";
// import ClientManager from "./ClientManager";
// import ClientAdmin from "./ClientAdmin";
import Button from "../Button";
import OperationalTax from "./OperationalTax";
import { formatAddress } from "rc-app/utils";
import { createClient } from "rc-app/api/agencyActionsApi";
// import { showErrorToast } from "rc-app/utils/toast";
import { handleApiError } from "rc-app/utils/apiErrorHandler";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { BackButton } from "../Buttons";
import axios from "axios";
import UserService from "rc-app/services/UserService";
// import { toast } from "react-toastify";

// Create address schema based on mode
const addressSchema = (isEditMode: boolean) => {
  if (isEditMode) {
    // Single string field for edit mode
    return yup.string().required("Address is required");
  } else {
    // Object with multiple fields for add mode
    return yup.object().shape({
      addressLine1: yup
        .string()
        .matches(
          /^[a-zA-Z0-9\s.,&-]+$/,
          "Address line 1 should contain only letters, numbers, spaces, and punctuation",
        )
        .required("Address line 1 is required"),
      addressLine2: yup
        .string()
        .matches(
          /^[a-zA-Z0-9\s.,&-]*$/,
          "Address line 2 should contain only letters, numbers, spaces, and punctuation",
        )
        .optional(),
      city: yup
        .string()
        .matches(
          /^[a-zA-Z\s.,&-]+$/,
          "City should contain only letters, spaces, and punctuation",
        )
        .required("City is required"),
      state: yup
        .string()
        .matches(
          /^[a-zA-Z\s.,&-]+$/,
          "State should contain only letters, spaces, and punctuation",
        )
        .required("State is required"),
      pincode: yup
        .string()
        .matches(/^\d{6}$/, "Pincode must be exactly 6 digits")
        .required("Pincode is required"),
      country: yup
        .string()
        .matches(
          /^[a-zA-Z\s.,&-]+$/,
          "Country should contain only letters, spaces, and punctuation",
        )
        .required("Country is required")
        .default("India"),
    });
  }
};

const invoiceSchema = yup.object().shape({
  value: yup
    .number()
    .min(0, "Value must be positive")
    .required("Invoice value is required"),
  currency_code: yup.string().required("Currency code is required"),
});

const taxSchema = yup.object().shape({
  value: yup
    .number()
    .min(0, "Value must be positive")
    .required("Tax value is required"),
  // currency_code: yup.string().required("Currency code is required"),
  // tax_rate: yup
  //   .number()
  //   .min(0)
  //   .max(100, "Tax rate must be between 0 and 100")
  //   .required("Tax rate is required"),
});

const clientAcreageAcquiredSchema = yup.object().shape({
  value: yup
    .number()
    .min(0, "Value must be positive")
    .required("Invoice value is required"),
  // unit: yup.string().required("Currency code is required"),
});

const clientSqftBuiltSchema = yup.object().shape({
  value: yup
    .number()
    .min(0, "Value must be positive")
    .required("Invoice value is required"),
  // unit: yup.string().required("Currency code is required"),
});

const formSchema = (isEditMode: boolean) => {
  return yup.object().shape({
    name: yup
      .string()
      .matches(
        /^[a-zA-Z\s.,&-]+$/,
        "Name should contain only letters, spaces, and punctuation",
      )
      .required("Agency name is required"),
    // region_id: yup.string().required("Region ID is required"),
    // address: addressSchema,
    address: addressSchema(isEditMode),
    logo: yup.string().url("Please enter a valid URL").optional(),
    agency_type: yup
      .string()
      .oneOf(["Developer", "Marketing"], "Invalid agency type")
      .required("Agency type is required"),
    incorporation_type: yup.string().required("Incorporation type is required"),
    incorporation_year: yup
      .string()
      .matches(/^\d{4}$/, "Year must be 4 digits")
      .required("Incorporation year is required"),
    footprint: yup
      .string()
      .oneOf(
        ["Single State", "Multi State", "National", "International"],
        "Invalid footprint",
      )
      .required("Footprint is required"),
    // area: yup.string().required("Area is required"),
    gstn: yup
      .string()
      .matches(
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
        "Invalid GSTN format ex: 29ABCDE1234F1Z5",
      )
      .required("GSTN is required"),
    pan: yup
      .string()
      .matches(
        /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
        "Invalid PAN format ex: AEAPZ5569J",
      )
      .required("PAN is required "),
    client_acreage_acquired: yup.object().shape({
      value: yup.string().required("Value is required"),
      // unit: yup.string().required("Unit is required"),
    }),
    client_sqft_built: yup.object().shape({
      value: yup.string().required("Value is required"),
      // unit: yup.string().required("Unit is required"),
    }),
    invoice: invoiceSchema,
    invoice_collected_to_date: invoiceSchema,
    tax_collected_to_date: taxSchema,
    tax_paid_to_date: taxSchema,
    channel_partner: yup.string().optional(),
  });
};

interface ValidationErrors {
  [key: string]: string;
}

const getFieldSchema = (
  path: string,
  isEditMode: boolean,
): yup.AnySchema | undefined => {
  const tempFormSchema = formSchema(isEditMode);
  const pathParts = path.split(".");

  // Direct field mapping
  if (path === "name") return tempFormSchema.fields.name as yup.AnySchema;
  if (path === "logo") return tempFormSchema.fields.logo as yup.AnySchema;
  if (path === "agency_type")
    return tempFormSchema.fields.agency_type as yup.AnySchema;
  if (path === "incorporation_type")
    return tempFormSchema.fields.incorporation_type as yup.AnySchema;
  if (path === "incorporation_year")
    return tempFormSchema.fields.incorporation_year as yup.AnySchema;
  if (path === "footprint")
    return tempFormSchema.fields.footprint as yup.AnySchema;
  if (path === "gstn") return tempFormSchema.fields.gstn as yup.AnySchema;
  if (path === "pan") return tempFormSchema.fields.pan as yup.AnySchema;
  if (path === "channel_partner")
    return tempFormSchema.fields.channel_partner as yup.AnySchema;

  // Address fields
  if (path === "address" && isEditMode) {
    // In edit mode, address is a string
    return tempFormSchema.fields.address as yup.AnySchema;
  } else if (path.startsWith("address.") && !isEditMode) {
    // In add mode, address is an object
    const field = pathParts[1];
    const addressSchema = tempFormSchema.fields
      .address as yup.ObjectSchema<any>;
    const addressFields = addressSchema.fields as any;
    return addressFields[field];
  }

  // Invoice fields
  if (path.startsWith("invoice.")) {
    const field = pathParts[1];
    const invoiceFields = invoiceSchema.fields as any;
    return invoiceFields[field];
  }

  if (path.startsWith("invoice_collected_to_date.")) {
    const field = pathParts[1];
    const invoiceFields = invoiceSchema.fields as any;
    return invoiceFields[field];
  }

  // Tax fields
  if (path.startsWith("tax_collected_to_date.")) {
    const field = pathParts[1];
    const taxFields = taxSchema.fields as any;
    return taxFields[field];
  }

  if (path.startsWith("tax_paid_to_date.")) {
    const field = pathParts[1];
    const taxFields = taxSchema.fields as any;
    return taxFields[field];
  }

  return undefined;
};

const steps = [
  { id: 1, name: "Client Details", component: ClientDetails },
  { id: 2, name: "Operational & Tax", component: OperationalTax },
  // { id: 3, name: "Client Manager", component: ClientManager },
  // { id: 4, name: "Client Admin", component: ClientAdmin },
];

const ClientFormWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editClientId = searchParams.get("edit");
  const isEditMode = !!editClientId;
  const prefetchedClient = (location.state as any)?.clientData;

  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState<any>({
    name: "",
    region_id: "68b53996ed2cf8175d432b5b",
    address: {
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      pincode: "",
      landmark: "",
      country: "",
    },
    logo: "",
    agency_type: "Developer",
    client_acreage_acquired: {
      value: "",
      unit: "sqft",
    },
    client_sqft_built: {
      value: "",
      unit: "sqft",
    },
    incorporation_type: "",
    incorporation_year: "",
    footprint: "Multi State",
    agency_subsidiary_names: [""],
    gstn: "",
    pan: "",
    invoice: {
      value: 0,
      currency_code: "INR",
    },
    invoice_collected_to_date: {
      value: 0,
      currency_code: "INR",
    },
    tax_collected_to_date: {
      value: 0,
      currency_code: "INR",
      tax_rate: 0,
    },
    tax_paid_to_date: {
      value: 0,
      currency_code: "INR",
      tax_rate: 0,
    },
    channel_partner: "",
    slug: "",
  });

  useEffect(() => {
    if (!isEditMode || !editClientId) return;

    const populateForm = (clientData: any) => {
      setFormData({
        name: clientData.name || "",
        region_id: clientData.region_id || "68b53996ed2cf8175d432b5b",
        address: clientData.address || "",
        logo: clientData.logo || "",
        agency_type: clientData.agency_type || "Developer",
        client_acreage_acquired: clientData.client_acreage_acquired || { value: "", unit: "sqft" },
        client_sqft_built: clientData.client_sqft_built || { value: "", unit: "sqft" },
        incorporation_type: clientData.incorporation_type || "",
        incorporation_year: clientData.incorporation_year || "",
        footprint: clientData.footprint || "Multi State",
        agency_subsidiary_names: Array.isArray(clientData.agency_subsidiary_names)
          ? clientData.agency_subsidiary_names.join(", ")
          : clientData.agency_subsidiary_names || "",
        gstn: clientData.gstn || "",
        pan: clientData.pan || "",
        invoice: clientData.invoice || { value: 0, currency_code: "INR" },
        invoice_collected_to_date: clientData.invoice_collected_to_date || { value: 0, currency_code: "INR" },
        tax_collected_to_date: clientData.tax_collected_to_date || { value: 0, currency_code: "INR", tax_rate: 0 },
        tax_paid_to_date: clientData.tax_paid_to_date || { value: 0, currency_code: "INR", tax_rate: 0 },
        channel_partner: clientData.channel_partner || "",
        slug: clientData.slug || "",
      });
    };

    if (prefetchedClient) {
      populateForm(prefetchedClient);
      return;
    }

    setLoading(true);
    const fetchClientData = async () => {
      try {
        const API_URL = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/realestate/views/re_agency/${editClientId}`;
        const response = await axios.get(API_URL, {
          headers: {
            Authorization: `Bearer ${UserService.getToken()}`,
            "ngrok-skip-browser-warning": "disabled",
            "Content-Type": "application/json",
          },
        });
        populateForm(response.data.agency);
      } catch (error) {
        console.error("Error fetching client data:", error);
        handleApiError(error, { resourceName: "Client" });
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [isEditMode, editClientId]);

  const flattenErrors = (yupError: any): ValidationErrors => {
    const flattened: ValidationErrors = {};

    if (yupError.inner) {
      yupError.inner.forEach((error: any) => {
        if (error.path) {
          flattened[error.path] = error.message;
        }
      });
    } else if (yupError.path) {
      flattened[yupError.path] = yupError.message;
    }

    return flattened;
  };

  const validateField = useCallback(
    async (path: string, value: any) => {
      try {
        const fieldSchema = getFieldSchema(path, isEditMode);

        if (fieldSchema) {
          await fieldSchema.validate(value);

          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[path];
            return newErrors;
          });
        }
      } catch (error: any) {
        setErrors((prev) => ({
          ...prev,
          [path]: error.message,
        }));
      }
    },
    [isEditMode],
  );

  const validateCurrentStep = async (): Promise<boolean> => {
    try {
      let partialSchema: yup.ObjectSchema<any> | undefined;
      let stepData: any;
      const tempFormSchema = formSchema(isEditMode);

      if (currentStep === 1) {
        partialSchema = yup.object().shape({
          name: tempFormSchema.fields.name as yup.StringSchema,
          agency_type: tempFormSchema.fields.agency_type as yup.StringSchema,
          channel_partner: tempFormSchema.fields
            .channel_partner as yup.StringSchema,
          footprint: tempFormSchema.fields.footprint as yup.StringSchema,
          logo: tempFormSchema.fields.logo as yup.StringSchema,
          address: addressSchema(isEditMode),
        });
        stepData = {
          name: formData.name,
          agency_type: formData.agency_type,
          channel_partner: formData.channel_partner,
          footprint: formData.footprint,
          logo: formData.logo,
          address: formData.address,
        };
      } else if (currentStep === 2) {
        // Operation & Tax tab validation
        partialSchema = yup.object().shape({
          incorporation_type: tempFormSchema.fields
            .incorporation_type as yup.StringSchema,
          incorporation_year: tempFormSchema.fields
            .incorporation_year as yup.StringSchema,
          client_acreage_acquired: clientAcreageAcquiredSchema,
          client_sqft_built: clientSqftBuiltSchema,
          gstn: tempFormSchema.fields.gstn as yup.StringSchema,
          pan: tempFormSchema.fields.pan as yup.StringSchema,
          invoice: invoiceSchema,
          invoice_collected_to_date: invoiceSchema,
          tax_collected_to_date: taxSchema,
          tax_paid_to_date: taxSchema,
        });
        stepData = {
          incorporation_type: formData.incorporation_type,
          incorporation_year: formData.incorporation_year,
          client_acreage_acquired: formData.client_acreage_acquired,
          client_sqft_built: formData.client_sqft_built,
          gstn: formData.gstn,
          pan: formData.pan,
          invoice: formData.invoice,
          invoice_collected_to_date: formData.invoice_collected_to_date,
          tax_collected_to_date: formData.tax_collected_to_date,
          tax_paid_to_date: formData.tax_paid_to_date,
        };
      }

      //  else if (currentStep === 2) {
      //   partialSchema = yup.object().shape({
      //     clientManager: formSchema.fields.clientManager as any,
      //   });
      //   stepData = {
      //     clientManager: formData.clientManager,
      //   };
      // }
      // else if (currentStep === 3) {
      //   partialSchema = yup.object().shape({
      //     clientAdmin: formSchema.fields.clientAdmin as any,
      //   });
      //   stepData = {
      //     clientAdmin: formData.clientAdmin,
      //   };
      // }

      if (partialSchema) {
        await partialSchema.validate(stepData, { abortEarly: false });
        setErrors({});
        return true;
      }
      return false;
    } catch (error: any) {
      const newErrors = flattenErrors(error);
      setErrors(newErrors);

      const stepFields = Object.keys(newErrors);
      const newTouched = new Set([...touched, ...stepFields]);
      setTouched(newTouched);

      return false;
    }
  };

  const handleNext = async () => {
    const isValid = await validateCurrentStep();
    if (isValid && currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleInputChange = async (e: any) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const parts = name.split(".");

      if (parts[0] === "address") {
        const addressField = parts[1];

        if (addressField === "pincode") {
          if (value === "" || /^\d*$/.test(value)) {
            const newValue = value.slice(0, 6);
            setFormData((prev: any) => ({
              ...prev,
              address: {
                ...prev.address,
                [addressField]: newValue,
              },
            }));
            // Validate immediately if field has been touched
            if (touched.has(name)) {
              await validateField(name, newValue);
            }
          }
          return;
        } else {
          setFormData((prev: any) => ({
            ...prev,
            address: {
              ...prev.address,
              [addressField]: value,
            },
          }));
          // Validate immediately if field has been touched
          if (touched.has(name)) {
            await validateField(name, value);
          }
        }
      } else {
        const [parentKey, childKey] = parts;
        setFormData((prev: any) => ({
          ...prev,
          [parentKey]: {
            ...prev[parentKey],
            [childKey]: value,
          },
        }));
        // Validate immediately if field has been touched
        if (touched.has(name)) {
          await validateField(name, value);
        }
      }
    } else {
      // Add input restrictions for specific fields
      if (name === "incorporation_year") {
        if (value === "" || /^\d*$/.test(value)) {
          const newValue = value.slice(0, 4);
          setFormData((prev: any) => ({
            ...prev,
            [name]: newValue,
          }));
          // Validate immediately if field has been touched
          if (touched.has(name)) {
            await validateField(name, newValue);
          }
        }
        return;
      } else if (name === "gstn") {
        const upperValue = value.toUpperCase();
        if (upperValue.length <= 15) {
          setFormData((prev: any) => ({
            ...prev,
            [name]: upperValue,
          }));
          // Validate immediately if field has been touched
          if (touched.has(name)) {
            await validateField(name, upperValue);
          }
        }
        return;
      } else if (name === "pan") {
        const upperValue = value.toUpperCase();
        if (upperValue.length <= 10) {
          setFormData((prev: any) => ({
            ...prev,
            [name]: upperValue,
          }));
          // Validate immediately if field has been touched
          if (touched.has(name)) {
            await validateField(name, upperValue);
          }
        }
        return;
      }

      setFormData((prev: any) => ({
        ...prev,
        [name]: value,
      }));

      // Validate immediately if field has been touched
      if (touched.has(name)) {
        await validateField(name, value);
      }
    }

    if (!touched.has(name)) {
      setTouched((prev) => new Set([...prev, name]));
    }
  };

  const handleBlur = async (fieldName: string) => {
    if (!touched.has(fieldName)) {
      setTouched((prev) => new Set([...prev, fieldName]));
    }

    // Validate field on blur
    if (fieldName.includes(".")) {
      const parts = fieldName.split(".");
      const [parentKey, childKey] = parts;
      await validateField(fieldName, formData[parentKey]?.[childKey]);
    } else {
      await validateField(fieldName, formData[fieldName]);
    }
  };

  const handleArrayInputChange = async (
    section: string,
    index: number,
    field: string,
    value: any,
  ) => {
    const path = `${section}[${index}].${field}`;

    let processedValue = value;

    if (field.includes("Phone")) {
      if (value === "" || /^\d*$/.test(value)) {
        processedValue = value.slice(0, 10);
      } else {
        return;
      }
    } else if (
      field === "cmName" ||
      field === "caName" ||
      field.includes("Username")
    ) {
      if (value === "" || /^[a-zA-Z0-9\s.,&-]*$/.test(value)) {
        processedValue = value;
      } else {
        return;
      }
    } else if (field.includes("Email")) {
      processedValue = value;
    } else if (field.includes("Password")) {
      processedValue = value;
    }

    setFormData((prev: any) => {
      const updatedArray = [...prev[section]];
      updatedArray[index] = {
        ...updatedArray[index],
        [field]: processedValue,
      };

      return {
        ...prev,
        [section]: updatedArray,
      };
    });

    await validateField(path, processedValue);

    if (!touched.has(path)) {
      setTouched((prev) => new Set([...prev, path]));
    }
  };

  const handleArrayBlur = (section: string, index: number, field: string) => {
    const path = `${section}[${index}].${field}`;
    if (!touched.has(path)) {
      setTouched((prev) => new Set([...prev, path]));
    }
  };

  const addManager = () => {
    setFormData((prev: any) => ({
      ...prev,
      clientManager: [
        ...prev.clientManager,
        {
          cmName: "",
          cmPhone: "",
          cmEmail: "",
          cmUsername: "",
          cmPassword: "",
        },
      ],
    }));
  };

  const removeManager = (index: number) => {
    const newErrors = { ...errors };
    Object.keys(newErrors).forEach((key) => {
      if (key.startsWith(`clientManager[${index}]`)) {
        delete newErrors[key];
      }
    });
    setErrors(newErrors);

    const newTouched = new Set(touched);
    Array.from(touched).forEach((key) => {
      if (key.startsWith(`clientManager[${index}]`)) {
        newTouched.delete(key);
      }
    });
    setTouched(newTouched);

    setFormData((prev: any) => {
      const updatedManagers = [...prev.clientManager];
      updatedManagers.splice(index, 1);
      return {
        ...prev,
        clientManager: updatedManagers,
      };
    });
  };

  const addAdmin = () => {
    setFormData((prev: any) => ({
      ...prev,
      clientAdmin: [
        ...prev.clientAdmin,
        {
          caName: "",
          caPhone: "",
          caEmail: "",
          caUsername: "",
          caPassword: "",
        },
      ],
    }));
  };

  const removeAdmin = (index: number) => {
    const newErrors = { ...errors };
    Object.keys(newErrors).forEach((key) => {
      if (key.startsWith(`clientAdmin[${index}]`)) {
        delete newErrors[key];
      }
    });
    setErrors(newErrors);

    const newTouched = new Set(touched);
    Array.from(touched).forEach((key) => {
      if (key.startsWith(`clientAdmin[${index}]`)) {
        newTouched.delete(key);
      }
    });
    setTouched(newTouched);

    setFormData((prev: any) => {
      const updatedAdmins = [...prev.clientAdmin];
      updatedAdmins.splice(index, 1);
      return {
        ...prev,
        clientAdmin: updatedAdmins,
      };
    });
  };

  // const handleSave = async () => {
  //   try {
  //     await formSchema.validate(formData, { abortEarly: false });

  //     console.log("Form submitted:", formData);

  //     // const existingClients = JSON.parse(
  //     //   localStorage.getItem("clients") || "[]"
  //     // );
  //     // const newClient = {
  //     //   id: Date.now().toString(),
  //     //   ...formData,
  //     //   address: formatAddress(
  //     //     formData.address.addressLine1,
  //     //     formData.address.addressLine2,
  //     //     formData.address.city,
  //     //     formData.address.state,
  //     //     formData.address.pincode,
  //     //     formData.address.country
  //     //   ),
  //     //   createdAt: new Date().toISOString(),
  //     // };

  //     const payloadFormData = {
  //       ...formData,
  //       // agency_subsidiary_names: [formData?.agency_subsidiary_names],
  //       agency_subsidiary_names: formData.agency_subsidiary_names
  //         .split(",")
  //         .map((name: string) => name.trim())
  //         .filter((name: string) => name !== ""),

  //       address: formatAddress(
  //         formData.address.addressLine1,
  //         formData.address.addressLine2,
  //         formData.address.city,
  //         formData.address.landmark,
  //         formData.address.state,
  //         formData.address.pincode,
  //         formData.address.country,
  //       ),
  //     };
  //     // localStorage.setItem(
  //     //   "clients",
  //     //   JSON.stringify([...existingClients, newClient])
  //     // );
  //     // createClient
  //     console.log("payloadFormData", payloadFormData);
  //     createClient(payloadFormData)
  //       .then((res: any) => {
  //         console.log("client created", res);
  //         navigate("/");
  //       })
  //       .catch((error) => {
  //         console.log("client Error", error);

  //         handleApiError(error, {
  //           resourceName: "Client",
  //           customMessages: {
  //             409: `Client with name "${formData.name}" already exists`,
  //           },
  //         });
  //       });
  //     // .catch((error) => {
  //     //   console.log("client Created Error", error);
  //     //   showErrorToast(error.message);

  //     // });

  //     alert(`Client "${formData.name}" saved successfully!`);

  //     // setFormData({
  //     //   clientName: "",
  //     //   clientType: "Developer",
  //     //   onboardedBy: "",
  //     //   clientFootprint: "Multi - State",
  //     //   clientLogo: "",
  //     //   address: {
  //     //     addressLine1: "",
  //     //     addressLine2: "",
  //     //     city: "",
  //     //     state: "",
  //     //     pincode: "",
  //     //     country: "India",
  //     //   },
  //     //   clientManager: [
  //     //     {
  //     //       cmName: "",
  //     //       cmPhone: "",
  //     //       cmEmail: "",
  //     //       cmUsername: "",
  //     //       cmPassword: "",
  //     //     },
  //     //   ],
  //     //   clientAdmin: [
  //     //     {
  //     //       caName: "",
  //     //       caPhone: "",
  //     //       caEmail: "",
  //     //       caUsername: "",
  //     //       caPassword: "",
  //     //     },
  //     //   ],
  //     // });

  //     // setFormData({ ...newClient });

  //     setErrors({});
  //     setTouched(new Set());
  //     setCurrentStep(1);
  //   } catch (error: any) {
  //     const newErrors = flattenErrors(error);
  //     setErrors(newErrors);

  //     const allFields = Object.keys(newErrors);
  //     const newTouched = new Set([...touched, ...allFields]);
  //     setTouched(newTouched);

  //     const firstErrorKey = Object.keys(newErrors)[0];
  //     if (firstErrorKey) {
  //       const element = document.querySelector(`[name="${firstErrorKey}"]`);
  //       if (element) {
  //         element.scrollIntoView({ behavior: "smooth", block: "center" });
  //       }
  //     }

  //     alert("Please fix the validation errors before saving.");
  //   }
  // };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const tempFormSchema = formSchema(isEditMode);

      await tempFormSchema.validate(formData, { abortEarly: false });

      const payloadFormData = {
        ...formData,
        agency_subsidiary_names: formData.agency_subsidiary_names
          .split(",")
          .map((name: string) => name.trim())
          .filter((name: string) => name !== ""),
        address: isEditMode
          ? formData.address
          : formatAddress(
              formData.address.addressLine1,
              formData.address.addressLine2,
              formData.address.city,
              formData.address.landmark,
              formData.address.state,
              formData.address.pincode,
              formData.address.country,
            ),
      };

      if (isEditMode && editClientId) {
        const API_URL = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/realestate/actions/re_agency/${editClientId}`;
        await axios.put(API_URL, payloadFormData, {
          headers: {
            Authorization: `Bearer ${UserService.getToken()}`,
            "ngrok-skip-browser-warning": "disabled",
            "Content-Type": "application/json",
          },
        });
      } else {
        await createClient(payloadFormData);
      }

      navigate("/");
      setErrors({});
      setTouched(new Set());
      setCurrentStep(1);
    } catch (error: any) {
      if (error.inner) {
        const newErrors = flattenErrors(error);
        setErrors(newErrors);
        const allFields = Object.keys(newErrors);
        const newTouched = new Set([...touched, ...allFields]);
        setTouched(newTouched);
      } else {
        handleApiError(error, {
          resourceName: "Client",
          customMessages: {
            409: `Client with name "${formData.name}" already exists`,
          },
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const hasStepErrors = useCallback(() => {
    if (currentStep === 1) {
      return Object.keys(errors).some(
        (key) =>
          key === "clientName" ||
          key === "clientType" ||
          key === "onboardedBy" ||
          key === "clientFootprint" ||
          key === "clientLogo" ||
          key.startsWith("address."),
      );
    } else if (currentStep === 2) {
      return Object.keys(errors).some((key) => key.startsWith("clientManager"));
    } else if (currentStep === 3) {
      return Object.keys(errors).some((key) => key.startsWith("clientAdmin"));
    }
    return false;
  }, [errors, currentStep]);

  const CurrentStepComponent = steps[currentStep - 1].component;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#1C2C3A]">
        <div className="w-16 h-16 border-4 border-[#008564] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      <div className="font-inter flex-grow bg-[#1C2C3A] min-h-screen p-3 sm:p-4 md:p-6">
        <div>
          {/* <button
            onClick={() => {
              navigate("/");
            }}
            className="ml-4 text-white flex items-center gap-2 hover:text-[#008564] transition-colors"
          >
            {`< Add New Client `}
          </button> */}
          <BackButton
            label={isEditMode ? "Edit Client" : "Add New Client"}
            // count={projects.length}
            navigateTo="/"
            className="font-inter"
          />
        </div>

        <div className="flex w-full items-center justify-center">
          {/* Step Indicator */}
          <div className="flex items-center bg-[#2E4051] px-4 py-2 rounded-lg gap-2">
            {steps.map((step: any, index: number) => (
              <div key={step.id} className="flex items-center">
                <Button
                  className={` px-3 py-2 xs:text-[7px] sm:text-[8px] md:text-[10px] lg:text-[12px] xl:text-[14px] rounded-md ${
                    currentStep === step.id
                      ? "bg-[#008564] text-white"
                      : "text-white"
                  }`}
                >
                  {step.name}
                </Button>
                {/* <div
                    className={`flex flex-col items-center ${
                      index < steps.length - 1 ? "flex-1" : ""
                    }`}
                  >
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                        currentStep === step.id
                          ? "bg-blue-600 border-blue-600 text-white"
                          : currentStep > step.id
                          ? "bg-green-500 border-green-500 text-white"
                          : "border-gray-300 text-gray-500"
                      }`}
                    >
                      {currentStep > step.id ? "✓" : step.id}
                    </div>
                    <span
                      className={`mt-2 text-sm font-medium ${
                        currentStep === step.id
                          ? "text-blue-600"
                          : "text-gray-500"
                      }`}
                    >
                      {step.name}
                    </span>
                  </div> */}

                {/* {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-2 ${
                        currentStep > step.id + 1
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                    ></div>
                  )} */}
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col w-full ">
          {/* Form Content */}
          <div className="flex-1 px-6 py-8">
            <CurrentStepComponent
              formData={formData}
              onInputChange={handleInputChange}
              onBlur={handleBlur}
              onArrayInputChange={handleArrayInputChange}
              onArrayBlur={handleArrayBlur}
              onAddManager={addManager}
              onRemoveManager={removeManager}
              onAddAdmin={addAdmin}
              onRemoveAdmin={removeAdmin}
              errors={errors}
              touched={touched}
              isEditMode={isEditMode} // Add this line
            />
          </div>

          {/* Footer - Navigation Buttons */}
          <div className="flex w-full justify-center gap-6 items-center px-6 py-4">
            <button
              type="button"
              onClick={() => {
                if (currentStep === 1) {
                  navigate("/");
                } else {
                  handleBack();
                }
              }}
              // disabled={currentStep === 1}
              className={`flex items-center justify-center px-6 py-2 rounded-md shadow-md ${
                currentStep === 1
                  ? "bg-[#2E4051] text-[#E7EEF9] hover:[#2E4051] hover:opacity-90"
                  : // ? "bg-[#2E4051] opacity-20 text-gray-400 cursor-not-allowed"
                    "bg-[#2E4051] text-[#E7EEF9] hover:[#2E4051] hover:opacity-90"
              }`}
            >
              {currentStep === 1 ? "Cancel" : "Back"}
            </button>

            <button
              type="button"
              onClick={currentStep === steps.length ? handleSave : handleNext}
              disabled={hasStepErrors() || isSaving}
              className={`flex items-center justify-center gap-2 px-6 py-2 rounded-md bg-[#008564] hover:opacity-90 text-white ${
                hasStepErrors() || isSaving ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isSaving && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {currentStep === steps.length ? (isSaving ? "Saving..." : "Save") : "Next"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClientFormWizard;
